import React, { FunctionComponent, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { usePostHog } from 'posthog-js/react';
import { cn } from '@companion-professional/webutils';
import { useCameraPreviewEnabled } from './state/camera';
import { WindowWithCheckupSettings } from './types/global';
import { Home } from './pages/Home';
import { GenerateError } from './components/GenerateError';
import { CheckupLoader } from './CheckupLoader';
import { trackEvent } from './lib/trackEvent';

declare let window: WindowWithCheckupSettings;

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const App: FunctionComponent = () => {
  const posthog = usePostHog();
  const location = useLocation();

  // The following is to handle routing from when this app was using hash routing.  The app is now using browser
  // routing, but we need to handle the case where a link was shared/sent/bookmarked with a hash in the URL.
  const navigate = useNavigate();
  useEffect(() => {
    if (location.hash.startsWith('#/')) {
      // Report the redirect event to PostHog so we can see how many people are still using hash routing.
      const realPath = location.hash.replace('#', '');
      trackEvent('hashroute_redirect', {
        from: location.hash,
        to: realPath
      });
      navigate(realPath);
    }
  }, []);

  // Because this is a Single Page App, the is needed to track pageviews in PostHog (as explained in this tutorial:
  // https://posthog.com/tutorials/single-page-app-pageviews)
  useEffect(() => {
    posthog.capture('$pageview');
  }, [location]);

  const cameraPreviewEnabled = useCameraPreviewEnabled();

  return (
    <div
      className={cn(
        // NOTE: overflow-x-hidden is necessary to prevent the page from doing a large horizontal scroll when the
        // instructions video player is active.  Not sure why this happens, but hiding the overflow-x fixes it.
        'flex w-full flex-1 flex-col items-center justify-between overflow-y-auto overflow-x-hidden',
        {
          // This is a hack to make the camera preview take up the full screen and push the header (tabs) off the screen.
          'landscape:h-[101vh]': cameraPreviewEnabled
        }
      )}
    >
      <SentryRoutes>
        <Route index element={<Home />} />
        <Route path="/generate-error" element={<GenerateError />} />
        <Route path="/:checkupId/*" element={<CheckupLoader />} />
      </SentryRoutes>
    </div>
  );
};
